import React from 'react'
import useTrans from '@hooks/useTrans'
import { cn } from '@utils/safeTs'
import { StatoPosizionePersonalizzazioneChoices, UserType } from '@utils/types'
import { Box, CheckCircle, Clock, Cog, Denied, DocumentUp, Hourglass, Image, Info, OctagonX, Project, Save, TimerPaused } from '@components/icons'
import Tooltip from '../Tooltip/Tooltip'
import styles from './StatoOrdine.module.sass'

export const StatoOrdineStatusPosizioneToOrdineMap = {
  'IN_ATTESA': 'IN_ATTESA',
  'LOGO_DA_CARICARE': 'LOGHI_DA_CARICARE',
  'BOZZA_DA_CARICARE': 'BOZZE_DA_CARICARE',
  'BOZZA_RIFIUTATA': 'BOZZE_RIFIUTATE',
  'BOZZA_SALVATA': 'BOZZE_SALVATE',
  'BOZZA_INVIATA': 'BOZZE_INVIATE',
  'BOZZA_APPROVATA': 'BOZZE_APPROVATE',
  'NUOVA_BOZZA_IN_LAVORAZIONE': 'NUOVA_BOZZA_IN_LAVORAZIONE',
  'IN_LAVORAZIONE': 'IN_LAVORAZIONE',
  'ORDINE_ANNULLATO': 'ORDINE_ANNULLATO',
  'ORDINE_EVASO': 'ORDINE_EVASO',
}

export type StatoOrdineStatus =
  | 'IN_ATTESA' 
  | 'TRASMESSO' // solo per livello ordine
  | 'IN_PREPARAZIONE' // solo per livello ordine
  | 'NEW_BLOCCATO'
  | 'LOGHI_DA_CARICARE' 
  | 'BOZZE_DA_CARICARE'
  | 'BOZZE_SALVATE'
  | 'BOZZE_INVIATE'
  | 'BOZZE_APPROVATE'
  | 'BOZZE_RIFIUTATE'
  | 'NUOVA_BOZZA_IN_LAVORAZIONE' // solo per livello posizione
  | 'IN_LAVORAZIONE' // solo per livello ordine
  | 'ORDINE_EVASO' // solo per livello ordine
  | 'ORDINE_ANNULLATO' // solo per livello ordine

export type StatoOrdineLevel = 'ordine' | 'posizione' | 'personalizzazione'

export interface StatoOrdineProps {
  stato?: StatoOrdineStatus | StatoPosizionePersonalizzazioneChoices
  level?: StatoOrdineLevel
  userType?: UserType
  styleVariant?: 'chip' | 'label'
  showLabel?: boolean
  className?: string
}

export const StatoOrdineStatusMap: { 
  [k in StatoOrdineStatus]: { 
    levels?: StatoOrdineLevel[],
    userTypes?: {
      [k2: string]: { 
        label?: string, 
        icon?: React.ReactNode, 
        theme?: 'warning' | 'error' | 'info' | 'accent' | 'grey' | 'success' 
        idle?: boolean
        tooltip?: string
      }
    }
  }} = {
  'IN_ATTESA': {
    levels: ['ordine', 'personalizzazione', 'posizione'],
    userTypes: {
      'default': {
        label: 'In attesa',
        icon: <Hourglass />,
        theme: 'grey',
      }
    }
  },
  'TRASMESSO': {
    levels: ['ordine'],
    userTypes: {
      'default': {
        label: 'Trasmesso',
        icon: <Hourglass />,
        theme: 'grey',
      }
    }
  },
  'IN_PREPARAZIONE': {
    levels: ['ordine'],
    userTypes: {
      'default': {
        label: 'In preparazione',
        icon: <Cog />,
        theme: 'accent',
      }
    }
  },
  'NEW_BLOCCATO': {
    levels: ['ordine'],
    userTypes: {
      'cliente': {
        label: 'In attesa',
        icon: <Hourglass />,
        theme: 'grey',
      },
      'default': {
        label: 'New - Sblocco AS',
        icon: <Hourglass />,
        theme: 'grey',
        idle: true,
      }
    }
  },
  'LOGHI_DA_CARICARE': {
    levels: ['personalizzazione', 'posizione', 'ordine'],
    userTypes: {
      'default': {
        label: 'Loghi da caricare',
        icon: <Image />,
        theme: 'warning',
      }
    }
  },
  'BOZZE_DA_CARICARE': {
    levels: ['ordine', 'posizione', 'personalizzazione'],
    userTypes: {
      'cliente': {
        label: 'Bozze in lavorazione',
        icon: <Project />,
        theme: 'grey',
        tooltip: 'La bozza di stampa è in preparazione. Appena sarà disponibile riceverai una notifica via mail.'
      },
      'default': {
        label: 'Bozze da caricare',
        icon: <DocumentUp />,
        theme: 'warning',
      },
    }
  },
  'BOZZE_SALVATE': {
    levels: ['ordine', 'posizione', 'personalizzazione'],
    userTypes: {
      'cliente': {
        label: 'Bozze in lavorazione',
        icon: <Project />,
        theme: 'grey',
        tooltip: 'La bozza di stampa è in preparazione. Appena sarà disponibile riceverai una notifica via mail.'
      },
      'default': {
        label: 'Bozze salvate',
        icon: <Save />,
        theme: 'info',
        idle: true,
      },
    }
  },
  'BOZZE_INVIATE': {
    levels: ['ordine', 'posizione', 'personalizzazione'],
    userTypes: {
      'cliente': {
        label: 'Bozze da approvare',
        icon: <TimerPaused />,
        theme: 'warning',
      },
      'default': {
        label: 'Bozze inviate',
        icon: <Clock />,
        theme: 'info',
      },
    }
  },
  'BOZZE_APPROVATE': {
    levels: ['ordine', 'posizione', 'personalizzazione'],
    userTypes: {
      'default': {
        label: 'Bozze approvate',
        icon: <CheckCircle />,
        theme: 'success',
      },
    }
  },
  'BOZZE_RIFIUTATE': {
    levels: ['ordine', 'posizione', 'personalizzazione'],
    userTypes: {
      'default': {
        label: 'Bozze rifiutate',
        icon: <OctagonX />,
        theme: 'error',
      },
    }
  },
  'NUOVA_BOZZA_IN_LAVORAZIONE': {
    levels: ['posizione'],
    userTypes: {
      'cliente': {
        label: 'Nuove bozze in lavorazione',
        icon: <Project />,
        theme: 'grey',
        tooltip: 'La nuova bozza di stampa è in preparazione. Appena sarà disponibile riceverai una notifica via mail.'
      },
    }
  },
  'IN_LAVORAZIONE': {
    levels: ['ordine', 'personalizzazione', 'posizione'],
    userTypes: {
      'default': {
        label: 'In lavorazione',
        icon: <Cog />,
        theme: 'accent',
      },
    },
  },
  'ORDINE_EVASO': {
    levels: ['ordine', 'personalizzazione', 'posizione'],
    userTypes: {
      'default': {
        label: 'Ordine evaso',
        icon: <Box />,
        theme: 'grey',
      },
    },
  },
  'ORDINE_ANNULLATO': {
    levels: ['ordine', 'personalizzazione'],
    userTypes: {
      'default': {
        label: 'Ordine annullato',
        icon: <Denied />,
        theme: 'grey',
      },
    },
  },
}

const StatoOrdine = ({
  stato = 'BOZZE_DA_CARICARE',
  level = 'ordine',
  userType = 'admin',
  styleVariant = 'chip',
  showLabel = true,
  className = '',
}: StatoOrdineProps) => {

  const t = useTrans()
  const ordineToPosizioneLabelMap = {
    'In attesa': 'In attesa',
    'Trasmesso': 'Trasmesso',
    'In preparazione': 'In preparazione',
    'Bozze in lavorazione': 'Bozza in lavorazione',
    'Loghi da caricare': 'Logo da caricare',
    'Bozze da caricare': 'Bozza da caricare',
    'Bozze salvate': 'Bozza salvata',
    'Bozze da approvare': 'Bozza da approvare',
    'Bozze inviate': 'Bozza inviata',
    'Bozze approvate': 'Bozza approvata',
    'Bozze rifiutate': 'Bozza rifiutata',
    'Nuove bozze in lavorazione': 'Nuova bozza in lavorazione',
    'In lavorazione': 'In lavorazione',
    'Ordine evaso': 'Ordine evaso',
    'Ordine annullato': 'Ordine annullato',
  }

  const finalStatus = level !== 'ordine' ? StatoOrdineStatusPosizioneToOrdineMap[stato] : stato
  const variant = StatoOrdineStatusMap?.[finalStatus]?.levels?.includes(level) ? StatoOrdineStatusMap?.[finalStatus]?.userTypes?.[userType] || StatoOrdineStatusMap?.[finalStatus]?.userTypes?.['default'] : null
  if (!variant) {
    return null
  }

  // La personalizzazione prende i plurali come l'ordine
  const label = showLabel ? level === 'posizione' ? ordineToPosizioneLabelMap[variant.label] || undefined : variant.label : undefined

  return styleVariant === 'label' ? (
    <>{label}</>
  ) : (
    <div
      className={cn(
        styles.root,
        label ? null : styles['root--noLabel'],
        variant.idle ? styles['root--idle'] : '',
        styles[`root--${variant.theme}`],
        className,
      )}
      role={label ? undefined : 'img'}
      aria-label={showLabel ? undefined : t(label)}
    >
      <span className={styles.icon} aria-hidden>
        {variant.icon}
      </span>
      {showLabel ? <span className={styles.label}>{t(label)}</span> : null}
      {variant.tooltip && showLabel ? (
        <span className={styles.tooltip}>
          <Tooltip 
            size="md" 
            pos={'top'} 
            label={t(variant.tooltip)}
            id={undefined} 
            className={undefined}
            containerClassName={undefined}
          >
            <Info className={styles.info} />
          </Tooltip>
        </span>
      ) : null}
    </div>
  )
}

export default StatoOrdine
